import { forwardRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { Country, DefaultInputComponentProps } from 'react-phone-number-input'
import Input, { getCountries } from 'react-phone-number-input/input'
import en from 'react-phone-number-input/locale/en.json'
import { SelectOption } from '../CreatableSelect'
import { ErrorLabel } from '../ErrorLabel'
import ReactSelectWrapper from '../ReactSelectWrapper'
import { TextInput } from '../TextInput'
import {
  DsTextField,
  FieldError,
  dsControlClass,
  ds2026SelectStyles,
} from '../../FbUI/Ds2026Field'
// (colors no longer needed here — country styles derive from ds2026SelectStyles)

// DS2026 styling for the compact country picker — the shared DS2026 select
// styles, with only the country-specific overrides: a fixed 36px control height
// (so it lines up with the adjacent phone/extension inputs) and tighter
// horizontal padding on the value container + caret.
const ds2026CountryStyles = {
  ...ds2026SelectStyles,
  control: (base: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...ds2026SelectStyles.control(base, state),
    height: 36,
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...ds2026SelectStyles.valueContainer(base),
    padding: '0 8px',
  }),
  dropdownIndicator: (
    base: Record<string, unknown>,
    state: { isFocused: boolean }
  ) => ({
    ...ds2026SelectStyles.dropdownIndicator(base, state),
    padding: '0 6px',
  }),
}

type CustomInputProps = DefaultInputComponentProps &
  React.RefAttributes<HTMLInputElement> & {
    placeholder?: string
  }

const CustomInput = forwardRef(
  (props: CustomInputProps, ref: React.ForwardedRef<HTMLInputElement>) => (
    <Form.Control
      {...props}
      placeholder={props.placeholder ?? 'Phone number'}
      ref={ref}
    />
  )
)
CustomInput.displayName = 'CustomInput'

// DS2026 phone input renders a plain native <input> (not a Bootstrap
// Form.Control) so it shares the exact DsInput metrics — the className passed
// in carries dsControlClass. Mixing Bootstrap + native controls was the source
// of the height/font drift across the phone row.
const Ds2026PhoneInput = forwardRef(
  (props: CustomInputProps, ref: React.ForwardedRef<HTMLInputElement>) => (
    <input
      {...props}
      placeholder={props.placeholder ?? 'Phone number'}
      ref={ref}
    />
  )
)
Ds2026PhoneInput.displayName = 'Ds2026PhoneInput'

const CountrySelect = ({
  value,
  onChange,
  ds2026 = false,
  ...rest
}: {
  value: Country
  onChange: (value: Country) => void
  labels?: Record<Country, string>
  ds2026?: boolean
}) => {
  const options: SelectOption[] = getCountries().map((country) => ({
    label: `${country}`,
    value: country,
  }))

  return (
    <ReactSelectWrapper
      {...rest}
      options={options}
      value={options.find((option) => option.value === value)}
      onChange={(option) =>
        onChange((option as SelectOption)?.value as Country)
      }
      // DS2026 picks the 36px DsInput height + DS2026 control look; the legacy
      // mode pins 38px to match the adjacent Bootstrap ``Form.Control`` (whose
      // natural height ≈ 38px) so the two controls align at the row baseline.
      height={ds2026 ? '36px' : '38px'}
      ds2026={ds2026}
      styles={
        ds2026
          ? {
              ...ds2026CountryStyles,
              container: (provided: Record<string, unknown>) => ({
                ...provided,
                minWidth: 100,
              }),
            }
          : {
              container: (provided) => ({
                ...provided,
                minWidth: 100,
              }),
            }
      }
    />
  )
}

export function PhoneInput<T extends FieldValues>({
  name,
  label,
  control,
  extensionName,
  optional = false,
  style,
  ds2026 = false,
}: {
  control: Control<T>
  name: Path<T>
  extensionName?: Path<T>
  label?: string
  optional?: boolean
  style?: React.CSSProperties
  /** Apply the DS2026 control look + 36px height to match DsInput fields. */
  ds2026?: boolean
}) {
  const {
    field: { ref, onChange, value, onBlur },
    fieldState: { error },
  } = useController({ name, control })
  const [country, setCountry] = useState<Country>('US')

  const handleCountryChange = (country: Country) => {
    setCountry(country)
    onChange(null)
  }

  const handlePhoneChange = (value: string | undefined) => {
    onChange(value || null)
  }

  return (
    <div>
      {label && (
        <label
          className="mb-2 text-base font-semibold text-gray-800 select-none"
          htmlFor={name}
        >
          {label}
          {optional && (
            <span className={'text-sm italic font-normal'}> - optional</span>
          )}
        </label>
      )}
      <div className="flex gap-2">
        <div className="flex-[3]">
          <div className="flex gap-2">
            <CountrySelect
              labels={en}
              value={country}
              onChange={handleCountryChange}
              ds2026={ds2026}
            />
            <div className="flex-1">
              <Input
                country={country}
                international
                withCountryCallingCode
                ref={ref}
                value={value}
                onChange={handlePhoneChange}
                onBlur={onBlur}
                inputComponent={ds2026 ? Ds2026PhoneInput : CustomInput}
                className={
                  ds2026
                    ? dsControlClass(
                        error?.message ? 'error' : 'default',
                        'regular',
                        'tabular-nums'
                      )
                    : error?.message
                      ? 'input-error'
                      : ''
                }
                // DS2026 leaves height to dsControlClass's min-h-9 (36px) so it
                // matches the DsInput fields exactly; legacy pins 38px to match
                // the Bootstrap Form.Control. Caller ``style`` still applies.
                style={ds2026 ? { ...style } : { height: '38px', ...style }}
              />
            </div>
          </div>
          {error?.message &&
            (ds2026 ? (
              <FieldError className="mt-1">{error.message}</FieldError>
            ) : (
              <ErrorLabel message={error?.message} />
            ))}
        </div>
        {extensionName && (
          <div className="flex-1">
            <ExtensionInput
              control={control}
              name={extensionName}
              ds2026={ds2026}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export function ExtensionInput<T extends FieldValues>({
  name,
  control,
  ds2026 = false,
}: {
  control: Control<T>
  name: Path<T>
  ds2026?: boolean
}) {
  if (ds2026) {
    return (
      <DsTextField
        control={control}
        name={name}
        placeholder="-Ext"
        inputClassName="tabular-nums"
      />
    )
  }
  return (
    <TextInput
      noMargin
      control={control}
      name={name}
      placeholder="-Ext"
      optional
    />
  )
}
